import { LocalContext, ModelBase } from '@skylab-shared/core';
import { AddressModel, ClientKind, ClientModel, DocumentBuyerModel, DocumentClientModel, OrderRecipientKind, OrderRecipientModel, TinKind } from '@skylab-shared/dataModel';
import { RecursiveDiff } from '@skylab-shared/types';
import { isUndefined } from '@skylab-shared/utils';
import { ClientRepositoryBase } from '../../client/client.repo-base';
import { ContextKeys } from '../../common/context-keys';
import { CountryItem } from '../../country/queries/country.query-base';

type ClientDocument = {
    client: DocumentClientModel | null;
    buyer?: DocumentBuyerModel | null;
};

type SellerDocument = {
    seller: DocumentClientModel | null;
    supplier?: DocumentBuyerModel | null;
};

type DocumentWithContractor = ClientDocument | SellerDocument;

export function fillDocumentClientBasedOnClient(documentClient: DocumentClientModel, client: ClientModel): void {
    documentClient.sourceClientId = client.id || null;
    documentClient.name = client.name;
    documentClient.kind = client.kind;
    documentClient.tinKind = client.tinKind;
    documentClient.internalIdentificationNumber = client.internalIdentificationNumber;
    documentClient.tin = client.tin;
    documentClient.emails = client.emails;

    const address = documentClient.address = new AddressModel();
    address.city = client.city;
    address.line1 = client.address;
    address.zipCode = client.zipCode;
    if (client.countryId) {
        address.countryId = client.countryId;
    }
}

export function fillDocumentClientBasedOnDocumentClient(target: RecursiveDiff<DocumentClientModel, ModelBase>, source: RecursiveDiff<DocumentClientModel, ModelBase>): void {
    if (source.address) {
        const address = new AddressModel();
        target.address = address;
        rewriteAddress(address, source.address);
    }

    target.kind = source.kind;
    target.sourceClientId = source.sourceClientId;
    target.name = source.name;
    target.tinKind = source.tinKind;
    target.internalIdentificationNumber = source.internalIdentificationNumber;
    target.tin = source.tin;
    target.emails = source.emails;
}

export function rewriteDocumentContractor(target: DocumentWithContractor, source: DocumentWithContractor): void {
    const sourceContractors = getContractorReferences(source);
    const targetContractors = getContractorReferences(target);

    if (!targetContractors.client && sourceContractors.client) {
        targetContractors.client = new DocumentClientModel();
        fillDocumentClientBasedOnDocumentClient(targetContractors.client, sourceContractors.client);
    }

    if (!targetContractors.buyer && sourceContractors.buyer) {
        targetContractors.buyer = new DocumentBuyerModel();
        fillDocumentClientBasedOnDocumentClient(targetContractors.buyer, sourceContractors.buyer);
        if (!isUndefined(sourceContractors.buyer.role)) targetContractors.buyer.role = sourceContractors.buyer.role;
    }
}

export async function rewriteDocumentContractorWithFreshDataAsync(target: DocumentWithContractor, source: DocumentWithContractor, clientRepository: ClientRepositoryBase): Promise<void> {
    const sourceContractors = getContractorReferences(source);
    const targetContractors = getContractorReferences(target);

    if (!sourceContractors.client) return;

    if (!sourceContractors.client.sourceClientId) {
        targetContractors.client = new DocumentClientModel();
        fillDocumentClientBasedOnDocumentClient(targetContractors.client, sourceContractors.client);

        if (sourceContractors.buyer) {
            targetContractors.buyer = new DocumentBuyerModel();

            if (sourceContractors.buyer?.sourceClientId) {
                const buyer = await clientRepository.findAsync(sourceContractors.buyer.sourceClientId);
                fillDocumentClientBasedOnClient(targetContractors.buyer, buyer);
            } else {
                fillDocumentClientBasedOnDocumentClient(targetContractors.buyer, sourceContractors.buyer);
            }

            if (!isUndefined(sourceContractors.buyer.role)) targetContractors.buyer.role = sourceContractors.buyer.role;
        }
    } else {
        let client: ClientModel;

        targetContractors.client = new DocumentClientModel();
        client = await clientRepository.findAsync(sourceContractors.client.sourceClientId);
        fillDocumentClientBasedOnClient(targetContractors.client, client);

        if (client.buyerId) {
            targetContractors.buyer = new DocumentBuyerModel();
            const buyer = await clientRepository.findAsync(client.buyerId);
            fillDocumentClientBasedOnClient(targetContractors.buyer, buyer);
            if (!isUndefined(client.buyerRole)) targetContractors.buyer.role = client.buyerRole;
        }
    }
}

export async function fillDocumentContractorBasedOnContractorId(target: DocumentWithContractor, clientRepository: ClientRepositoryBase, clientId: number): Promise<void> {
    if (!clientId) return;
    const targetContractors = getContractorReferences(target);

    const clientModel = await clientRepository.findAsync(clientId);
    const documentClient = new DocumentClientModel();
    fillDocumentClientBasedOnClient(documentClient, clientModel);
    targetContractors.client = documentClient;

    if (clientModel.buyerId) {
        const buyerModel = await clientRepository.findAsync(clientModel.buyerId);
        targetContractors.buyer = new DocumentBuyerModel();
        fillDocumentClientBasedOnClient(targetContractors.buyer, buyerModel);
        if (!isUndefined(clientModel.buyerRole)) targetContractors.buyer.role = clientModel.buyerRole;
    } else if (targetContractors.buyer){
        targetContractors.buyer = null;
    }
}

export function fillDocumentClientFromOrderClient(target: DocumentClientModel, source: OrderRecipientModel | null, countries: CountryItem[]): void {
    if (!source) return;

    if (source.address) {
        const address = new AddressModel();
        target.address = address;
        target.address.city = source.address.city;
        const country = countries.find(e => e.externalId === source.address?.countryExternalId);
        if (country) {
            target.address.countryId = country.id;
        }
        target.address.line1 = source.address.line1;
        target.address.line2 = source.address.line2;
        if (source.kind === OrderRecipientKind.Business) {
            target.name = source.companyName;
            target.kind = ClientKind.Company;
            target.tinKind = TinKind.NIP;
        } else {
            target.name = `${source.firstName} ${source.lastName}`;
            target.kind = ClientKind.Person;
            target.tinKind = TinKind.PESEL;
        }
        target.address.zipCode = source.address.zipCode;
    }

    target.tin = source.taxIdentification;
    target.emails = [source.email];
}

export function rewriteAddress(target: RecursiveDiff<AddressModel, ModelBase>, source: RecursiveDiff<AddressModel, ModelBase>): void {
    target.city = source.city;
    target.countryId = source.countryId;
    target.line1 = source.line1;
    target.name = source.name;
    target.zipCode = source.zipCode;
}

export async function getUpdatedBuyerAsync(clientModel: ClientModel, localContext: LocalContext, clientRepository: ClientRepositoryBase): Promise<DocumentBuyerModel | null> {
    if (!clientModel?.buyerId) return null;

    const getRawContractorAsync = async (id: number) => await localContext.getAsync(ContextKeys.client(id), () => clientRepository.findAsync(id));

    const rawBuyer = await getRawContractorAsync(clientModel.buyerId);
    const newBuyer = new DocumentBuyerModel();
    fillDocumentClientBasedOnClient(newBuyer, rawBuyer);
    if (!isUndefined(clientModel.buyerRole)) newBuyer.role = clientModel.buyerRole;

    return newBuyer;
}

export function fillClientBasedOnDocumentClient(client: ClientModel, documentClient: DocumentClientModel): void {
    if (documentClient.sourceClientId) {
        client.id = documentClient.sourceClientId;
    }

    client.kind = documentClient.kind;
    client.tin = documentClient.tin;
    client.tinKind = documentClient.tinKind;
    client.internalIdentificationNumber = documentClient.internalIdentificationNumber;
    client.name = documentClient.name;
    client.emails = documentClient.emails || [];

    if (documentClient.address) {
        client.city = documentClient.address.city;
        client.countryId = documentClient.address.countryId;
        client.address = documentClient.address.line1;
        client.zipCode = documentClient.address.zipCode;
    }
}

const getContractorReferences = (document: DocumentWithContractor) => {
    if ('seller' in document) {
        return {
            get client() {
                return document.seller;
            },
            set client(value) {
                document.seller = value;
            },

            get buyer() {
                return document.supplier;
            },
            set buyer(value) {
                document.supplier = value;
            },
        };
    }

    return {
        get client() {
            return document.client;
        },
        set client(value) {
            document.client = value!;
        },

        get buyer() {
            return document.buyer;
        },
        set buyer(value) {
            document.buyer = value;
        },
    };
};